  (function () {
    class JffdBundleForm extends HTMLElement {
      static #footerPaddingVar = '--jffd-bundle-sticky-bar-footer-padding-bottom';

      // Clean selling-plan name for the _bundle_selling_plan_name property.
      // Prefer the Shopify plan name stamped on the <option> (data-plan-name);
      // fall back to the option text with whitespace collapsed and a trailing
      // "(recommended)"-style suffix removed.
      static planNameFromOption(option) {
        if (!option) return '';
        const dataName = option.dataset?.planName?.trim();
        if (dataName) return dataName;
        return (option.textContent || '')
          .replace(/\s+/g, ' ')
          .replace(/\s*\([^)]*\)\s*$/, '')
          .trim();
      }

      #abortController = new AbortController();
      #stickyObs = null;
      #stickyBarResizeObserver = null;

      connectedCallback() {
        const { signal } = this.#abortController;

        // Size selector
        this.querySelectorAll('.jffd-bundle-product__size-radio').forEach((radio) => {
          radio.addEventListener('change', () => this.#handleSizeChange(radio), { signal });
        });

        // Recipe tile +/- buttons
        this.querySelectorAll('[data-tile-add]').forEach((btn) => {
          const tile = btn.closest('.jffd-bundle-product__recipe-tile');
          btn.addEventListener('click', () => this.#handleTileAdd(tile), { signal });
        });
        this.querySelectorAll('[data-tile-remove]').forEach((btn) => {
          const tile = btn.closest('.jffd-bundle-product__recipe-tile');
          btn.addEventListener('click', () => this.#handleTileRemove(tile), { signal });
        });

        // ATC buttons (inline builder + sticky bar)
        this.querySelectorAll('[data-atc-button]').forEach((btn) => {
          btn.addEventListener('click', (e) => this.#handleAddToCart(e), { signal });
        });

        // Purchase type radios — update running total and size card prices on toggle
        this.querySelectorAll('.jffd-bundle-product__purchase-radio').forEach((radio) => {
          radio.addEventListener('change', () => {
            this.#syncPriceDisplay();
            this.#syncSizeCardPrices();
            this.#updateRunningTotal();
          }, { signal });
        });

        // Gallery thumbnails (now inside the custom element)
        this.#initGallery();

        // Sticky bar visibility observer
        this.#initStickyBar();

        // Keep footer padding in sync with sticky bar height on mobile
        this.#setupFooterBottomPaddingSync();
        window.addEventListener('resize', this.#syncFooterBottomPadding, { signal });
        this.#syncFooterBottomPadding();

        // Sync initial state
        this.#syncProgressTicks(this.#getSlotCount());
        this.#updateTally();
        this.#syncPurchaseTypeVisibility();
        this.#syncPriceDisplay();
        this.#initStartingPrice();

        // Check URL for variant selection or edit mode
        this.#checkUrlParams();

        // Pre-populate builder if arriving via an edit link.
        this.#checkEditMode();
      }

      disconnectedCallback() {
        this.#abortController.abort();
        this.#stickyObs?.disconnect();
        this.#stickyBarResizeObserver?.disconnect();
        this.#clearFooterBottomPadding();
      }

      // ── Gallery ────────────────────────────────────────────────────────────────

      #initGallery() {
        const featured = this.querySelector('.jffd-bundle-product__featured-image');
        const thumbs = this.querySelectorAll('.jffd-bundle-product__thumb');
        const { signal } = this.#abortController;
        thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            if (featured) {
              featured.src = thumb.dataset.mediaSrc;
              featured.alt = thumb.dataset.mediaAlt || '';
            }
            thumbs.forEach((t) => t.classList.remove('jffd-bundle-product__thumb--active'));
            thumb.classList.add('jffd-bundle-product__thumb--active');
          }, { signal });
        });
      }

      // ── Sticky bar ─────────────────────────────────────────────────────────────

      #initStickyBar() {
        const trigger = this.querySelector('[data-sticky-trigger]');
        const stickyBar = this.querySelector('[data-sticky-bar]');
        if (!trigger || !stickyBar) return;
        this.#stickyObs = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) {
              stickyBar.dataset.visible = '';
            } else {
              delete stickyBar.dataset.visible;
            }
            this.#syncFooterBottomPadding();
          },
          { threshold: 0 }
        );
        this.#stickyObs.observe(trigger);
      }

      #isMobileViewport() {
        return window.matchMedia('(max-width: 990px)').matches;
      }

      #setupFooterBottomPaddingSync() {
        const stickyBar = this.querySelector('[data-sticky-bar]');
        if (!stickyBar || typeof ResizeObserver === 'undefined') return;

        this.#stickyBarResizeObserver?.disconnect();
        this.#stickyBarResizeObserver = new ResizeObserver(() => {
          this.#syncFooterBottomPadding();
        });
        this.#stickyBarResizeObserver.observe(stickyBar);
      }

      #syncFooterBottomPadding = () => {
        const stickyBar = this.querySelector('[data-sticky-bar]');
        if (!this.#isMobileViewport() || !stickyBar) {
          this.#clearFooterBottomPadding();
          return;
        }

        const height = Math.ceil(stickyBar.getBoundingClientRect().height);
        if (height > 0) {
          document.documentElement.style.setProperty(JffdBundleForm.#footerPaddingVar, `${height}px`);
        }
      };

      #clearFooterBottomPadding() {
        document.documentElement.style.removeProperty(JffdBundleForm.#footerPaddingVar);
      }

      // ── Getters ────────────────────────────────────────────────────────────────

      #getSlotCount() {
        return parseInt(this.dataset.slotCount, 10) || 14;
      }

      #getCurrentPool() {
        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        return checkedSize ? checkedSize.dataset.recipePool : '18oz';
      }

      #getPackUnitCount() {
        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        return parseInt(checkedSize?.dataset.packUnitCount, 10) || 7;
      }

      #selectedVariantHasSellingPlan() {
        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        return checkedSize?.dataset.hasSellingPlan === 'true';
      }

      // ── Discount calculation ───────────────────────────────────────────────────

      #getDiscountPercent() {
        const discountUsed = this.dataset.discountUsed === 'true';
        if (discountUsed) {
          return Number(this.dataset.discountPercent) || 0;
        }
        return Number(this.dataset.initialDiscountPercent) || 0;
      }

      #getThresholdCents() {
        return Number(this.dataset.thresholdCents) || 0;
      }

      #applyDiscount(priceCents) {
        if (!priceCents || priceCents <= 0) return priceCents;

        const discountPercent = this.#getDiscountPercent();
        if (!discountPercent || discountPercent <= 0) return priceCents;

        // Truncate rather than round: e.g. 5% of $12.99 is $0.6495, and the
        // discount taken should be $0.64, not rounded up to $0.65.
        let discountAmount = Math.floor((priceCents * discountPercent) / 100);
        const thresholdCents = this.#getThresholdCents();

        if (thresholdCents > 0 && discountAmount > thresholdCents) {
          discountAmount = thresholdCents;
        }

        return priceCents - discountAmount;
      }

      // True when the subscribe discount on priceCents would exceed the
      // configured max-discount threshold (i.e. #applyDiscount caps it).
      #isDiscountCapped(priceCents) {
        if (!priceCents || priceCents <= 0) return false;

        const discountPercent = this.#getDiscountPercent();
        if (!discountPercent || discountPercent <= 0) return false;

        const discountAmount = Math.floor((priceCents * discountPercent) / 100);
        const thresholdCents = this.#getThresholdCents();
        return thresholdCents > 0 && discountAmount > thresholdCents;
      }

      // Show/hide the "max threshold discount" message below the size grid.
      // Called on every subscription price recalculation (size change, purchase
      // type toggle, recipe tile add/remove) so it always reflects the live price.
      #updateThresholdMessage(priceCents, showSubscribePrice) {
        const thresholdMessage = this.querySelector('[data-threshold-message]');
        if (!thresholdMessage) return;
        thresholdMessage.hidden = !(showSubscribePrice && this.#isDiscountCapped(priceCents));
      }

      #syncPurchaseTypeVisibility() {
        const purchaseTypeBlock = this.querySelector('[data-purchase-type-block]');
        if (!purchaseTypeBlock) return;

        const hasSellingPlan = this.#selectedVariantHasSellingPlan();
        purchaseTypeBlock.hidden = !hasSellingPlan;
        if (!hasSellingPlan) {
          document.querySelector('[data-purchase-type-block]')?.setAttribute('hidden', 'true');
        }

        const purchaseRadios = this.querySelectorAll('.jffd-bundle-product__purchase-radio');
        purchaseRadios.forEach((radio) => {
          radio.disabled = !hasSellingPlan;
          radio.setAttribute('aria-disabled', String(!hasSellingPlan));
        });

        if (!hasSellingPlan) {
          const oneTimeRadio = this.querySelector('.jffd-bundle-product__purchase-radio[value="one-time"]');
          if (oneTimeRadio) oneTimeRadio.checked = true;
        }
      }

      #getVisibleTiles() {
        const pool = this.#getCurrentPool();
        const poolEl = this.querySelector(`.jffd-bundle-product__recipe-pool[data-recipe-pool="${pool}"]`);
        if (!poolEl) return [];
        return Array.from(poolEl.querySelectorAll('.jffd-bundle-product__recipe-tile'));
      }

      #getCurrentTally() {
        return this.#getVisibleTiles().reduce(
          (sum, tile) => sum + (parseInt(tile.dataset.qty, 10) || 0),
          0
        );
      }

      // ── Size change ─────────────────────────────────────────────────────────────

      #handleSizeChange(radio) {
        // Update URL to preserve variant selection on page refresh
        this.#updateUrlVariant(radio.dataset.variantId);

        // Update host element attributes
        this.dataset.parentVariantId = radio.dataset.variantId;
        this.dataset.slotCount = radio.dataset.slotCount;
        this.dataset.productTitle = radio.dataset.variantTitle || this.dataset.productTitle || '';

        // Toggle purchase type block based on this size variant's selling plan availability
        this.#syncPurchaseTypeVisibility();

        // Update tally totals (builder header + sticky bar)
        this.querySelectorAll('[data-tally-total]').forEach((el) => {
          el.textContent = radio.dataset.slotCount;
        });

        // Swap recipe pool visibility
        this.querySelectorAll('.jffd-bundle-product__recipe-pool[data-recipe-pool]').forEach((poolEl) => {
          if (poolEl.dataset.recipePool === radio.dataset.recipePool) {
            poolEl.removeAttribute('hidden');
          } else {
            poolEl.setAttribute('hidden', '');
          }
        });

        // Reset all tile allocations across all pools
        this.querySelectorAll('.jffd-bundle-product__recipe-tile').forEach((tile) => {
          tile.dataset.qty = '0';
          const badge = tile.querySelector('[data-qty-badge]');
          if (badge) badge.textContent = '0';
          const qtyDisplay = tile.querySelector('[data-qty-display]');
          if (qtyDisplay) qtyDisplay.textContent = '0';
        });

        // Reset progress fills and completion state
        this.querySelectorAll('[data-progress-fill]').forEach((el) => {
          el.style.width = '0%';
        });
        this.querySelectorAll('[data-progress-track]').forEach((track) => {
          delete track.dataset.complete;
        });

        // Rebuild ticks for the new slot count
        this.#syncProgressTicks(parseInt(radio.dataset.slotCount, 10) || 14);

        // Sync "From $X" prices in purchase options and size cards
        this.#syncPriceDisplay();
        this.#initStartingPrice();

        // Recalculate tally (also syncs per-tile button states + running total)
        this.#updateTally();
      }

      // ── Progress ticks ─────────────────────────────────────────────────────────

      #syncProgressTicks(slotCount) {
        const increment = 7;
        this.querySelectorAll('[data-progress-track]').forEach((track) => {
          track.querySelectorAll('.jffd-bundle-product__progress-tick').forEach((t) => t.remove());
          for (let i = increment; i < slotCount; i += increment) {
            const tick = document.createElement('span');
            tick.className = 'jffd-bundle-product__progress-tick';
            tick.setAttribute('aria-hidden', 'true');
            tick.style.left = `${(i / slotCount) * 100}%`;
            track.appendChild(tick);
          }
        });
      }

      // ── Tile interactions ───────────────────────────────────────────────────────

      #handleTileAdd(tile) {
        if (!tile) return;
        const current = parseInt(tile.dataset.qty, 10) || 0;
        const tally = this.#getCurrentTally();
        const slotCount = this.#getSlotCount();
        const packUnitCount = this.#getPackUnitCount();
        if (tally + packUnitCount > slotCount) return;
        const newQty = current + packUnitCount;
        tile.dataset.qty = String(newQty);
        const badge = tile.querySelector('[data-qty-badge]');
        if (badge) badge.textContent = String(newQty);
        const qtyDisplay = tile.querySelector('[data-qty-display]');
        if (qtyDisplay) qtyDisplay.textContent = String(newQty);
        tile.classList.add('jffd-bundle-product__recipe-tile--just-added');
        setTimeout(() => tile.classList.remove('jffd-bundle-product__recipe-tile--just-added'), 450);
        this.#updateTally();
      }

      #handleTileRemove(tile) {
        if (!tile) return;
        const current = parseInt(tile.dataset.qty, 10) || 0;
        if (current === 0) return;
        const packUnitCount = this.#getPackUnitCount();
        const newQty = Math.max(current - packUnitCount, 0);
        tile.dataset.qty = String(newQty);
        const badge = tile.querySelector('[data-qty-badge]');
        if (badge) badge.textContent = String(newQty);
        const qtyDisplay = tile.querySelector('[data-qty-display]');
        if (qtyDisplay) qtyDisplay.textContent = String(newQty);
        this.#updateTally();
      }

      // ── Tally + button sync ─────────────────────────────────────────────────────

      #updateTally() {
        const tally = this.#getCurrentTally();
        const slotCount = this.#getSlotCount();
        const packUnitCount = this.#getPackUnitCount();
        const pct = slotCount > 0 ? Math.min((tally / slotCount) * 100, 100) : 0;

        // Update tally counters (builder header + sticky bar)
        this.querySelectorAll('[data-tally-count]').forEach((el) => {
          el.textContent = String(tally);
        });

        // Update progress fills (builder header + sticky bar)
        this.querySelectorAll('[data-progress-fill]').forEach((el) => {
          el.style.width = `${pct}%`;
        });

        // Gate ATC on exact fill
        const isFull = tally === slotCount;
        this.querySelectorAll('[data-atc-button]').forEach((btn) => {
          btn.disabled = !isFull;
          btn.setAttribute('aria-disabled', String(!isFull));
        });

        // Completion pulse on the progress track
        this.querySelectorAll('[data-progress-track]').forEach((track) => {
          if (isFull) {
            track.dataset.complete = '';
          } else {
            delete track.dataset.complete;
          }
        });

        const progressBuilder = this.querySelector('.jffd-bundle-product__builder');
        const recipeTotal = Math.ceil(slotCount / packUnitCount);
        const progressMessage =
          tally === 0
            ? (progressBuilder?.dataset.progressMessageEmpty || 'Choose [count] recipes to build your bundle')
                .replaceAll('[count]', String(recipeTotal))
                .replaceAll('{{ count }}', String(recipeTotal))
            : `${tally} / ${slotCount} packages selected`;
        this.querySelectorAll('[data-progress-message]').forEach((el) => {
          el.textContent = progressMessage;
        });
        this.querySelectorAll('[data-sticky-tally-message]').forEach((el) => {
          el.textContent = progressMessage;
        });

        // Per-tile add/remove button states
        const atCapacity = tally >= slotCount;
        this.#getVisibleTiles().forEach((tile) => {
          const addBtn = tile.querySelector('[data-tile-add]');
          const removeBtn = tile.querySelector('[data-tile-remove]');
          const qty = parseInt(tile.dataset.qty, 10) || 0;
          if (addBtn) {
            addBtn.disabled = atCapacity;
            addBtn.setAttribute('aria-disabled', String(atCapacity));
          }
          if (removeBtn) {
            removeBtn.disabled = qty === 0;
            removeBtn.setAttribute('aria-disabled', String(qty === 0));
          }
        });

        // Update live running total
        this.#updateRunningTotal();
      }

      // ── Price helpers ───────────────────────────────────────────────────────────

      #formatPrice(cents) {
        const roundedCents = Math.round(cents);
        const currency =
          (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'USD';
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(roundedCents / 100);
        } catch (_) {
          return `$${(roundedCents / 100).toFixed(2)}`;
        }
      }

      // Sync "From $X" values: subscribe/one-time toggle detail + all size card prices.
      // Called on size change and purchase type toggle.
      #syncPriceDisplay() {
        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        if (!checkedSize) return;

        const startingAt = parseInt(checkedSize.dataset.startingAt, 10) || 0;
        const startingAtSubscribe = this.#applyDiscount(startingAt);
        const savingsPct = checkedSize.dataset.savingsPct;
        const packUnitCount = this.#getPackUnitCount();

        this.querySelectorAll('[data-subscribe-price]').forEach((el) => {
          el.textContent = this.#formatPrice(startingAtSubscribe);
        });
        this.querySelectorAll('[data-one-time-price]').forEach((el) => {
          el.textContent = this.#formatPrice(startingAt);
        });
        this.querySelectorAll('[data-subscribe-badge]').forEach((el) => {
          el.textContent =
            savingsPct && Number(savingsPct) > 0 ? `${savingsPct}% off` : '';
        });

        // Native JFFD selling-plan-selector "starting at" prices (before any recipes
        // are selected); overwritten with the live running total once tiles are picked.
        this.querySelectorAll('[data-bundle-price="one-time"]').forEach((el) => {
          el.textContent = this.#formatPrice(startingAt);
        });
        this.querySelectorAll('[data-bundle-price="subscription"]').forEach((el) => {
          el.textContent = this.#formatPrice(startingAtSubscribe);
        });
      }

      // ── Initialize starting price on load ───────────────────────────────────────

      #initStartingPrice() {
        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        if (!checkedSize) return;

        const startingAt = parseInt(checkedSize.dataset.startingAt, 10) || 0;
        const startingAtSubscribe = this.#applyDiscount(startingAt);

        // Include "From" text since no recipes are selected
        const regularText = startingAt > 0 ? `From ${this.#formatPrice(startingAt)}` : 'From —';
        const subscriptionText = startingAtSubscribe > 0 ? `From ${this.#formatPrice(startingAtSubscribe)}` : 'From —';

        this.querySelectorAll('[data-bundle-price="one-time"]').forEach((el) => {
          el.textContent = regularText;
        });
        this.querySelectorAll('[data-bundle-price="subscription"]').forEach((el) => {
          el.textContent = subscriptionText;
        });

        this.#syncSizeCardPrices();
      }

      // Updates every size card's price (not just the selected one) to match the
      // Subscribe & Save block: discounted sale price + strikeout while "Subscribe"
      // is the active purchase type, plain price with no strikeout on "Ship Once".
      #syncSizeCardPrices() {
        const showSubscribePrice = this.#getPurchaseType() === 'subscribe';

        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        const checkedStartingAt = parseInt(checkedSize?.dataset.startingAt, 10) || 0;
        this.#updateThresholdMessage(checkedStartingAt, showSubscribePrice);

        this.querySelectorAll('.jffd-bundle-product__size-radio').forEach((sizeRadio) => {
          const sizeOption = sizeRadio.closest('.jffd-bundle-product__size-option');
          const startingAt = parseInt(sizeRadio.dataset.startingAt, 10) || 0;
          const salePrice = showSubscribePrice ? this.#applyDiscount(startingAt) : startingAt;
          const showCompare = showSubscribePrice && startingAt > salePrice;

          sizeOption?.querySelectorAll('[data-size-price-from]').forEach((el) => {
            el.hidden = false;
          });
          sizeOption?.querySelectorAll('[data-size-price]').forEach((el) => {
            el.textContent = this.#formatPrice(salePrice);
          });
          sizeOption?.querySelectorAll('[data-size-price-compare]').forEach((el) => {
            el.textContent = showCompare ? this.#formatPrice(startingAt) : '';
            el.hidden = !showCompare;
          });
        });
      }

      // ── Running total ───────────────────────────────────────────────────────────

      #updateRunningTotal() {
        const purchaseType = this.#getPurchaseType();

        // Calculate regular (one-time) price total
        const regularTotalCents = this.#getVisibleTiles().reduce((sum, tile) => {
          const qty = parseInt(tile.dataset.qty, 10) || 0;
          const unitPrice = parseInt(tile.dataset.unitPrice, 10) || 0;
          return sum + qty * unitPrice;
        }, 0);

        // Match the standard product price: calculate the subscription preview
        // from the regular component price using the theme-configured discount.
        // Apply the discount once to the bundle total rather than per unit price —
        // discounting per unit and then scaling by qty compounds rounding error.
        const subscriptionTotalCents = this.#applyDiscount(regularTotalCents);

        // Display in sticky bar running total
        const activeTotal = purchaseType === 'subscribe' ? subscriptionTotalCents : regularTotalCents;
        this.querySelectorAll('[data-live-total]').forEach((el) => {
          el.textContent = activeTotal > 0 ? this.#formatPrice(activeTotal) : '—';
        });

        // Nothing picked yet: keep the "From $X" starting prices (strikethrough + sale)
        // instead of blanking them out.
        if (this.#getCurrentTally() === 0) {
          this.#initStartingPrice();
          return;
        }

        // Live subscription total just changed (recipe added/removed) — re-check
        // whether the discount on it is capped by the threshold.
        this.#updateThresholdMessage(regularTotalCents, purchaseType === 'subscribe');

        // Selected size card mirrors the Subscribe & Save block's live total as
        // recipes are added: discounted price + strikeout while subscribing,
        // plain price with no strikeout on Ship Once.
        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        const selectedSizeOption = checkedSize?.closest('.jffd-bundle-product__size-option');
        const showSubscribePrice = purchaseType === 'subscribe';
        const selectedSalePrice = showSubscribePrice ? subscriptionTotalCents : regularTotalCents;
        const showSelectedCompare = showSubscribePrice && regularTotalCents > subscriptionTotalCents;
        selectedSizeOption?.querySelectorAll('[data-size-price-from]').forEach((el) => {
          el.hidden = true;
        });
        selectedSizeOption?.querySelectorAll('[data-size-price]').forEach((el) => {
          el.textContent = this.#formatPrice(selectedSalePrice);
        });
        selectedSizeOption?.querySelectorAll('[data-size-price-compare]').forEach((el) => {
          el.textContent = showSelectedCompare ? this.#formatPrice(regularTotalCents) : '';
          el.hidden = !showSelectedCompare;
        });

        // Native JFFD selling-plan-selector: show the live bundle total, no "From" prefix.
        const regularLiveText = regularTotalCents > 0 ? this.#formatPrice(regularTotalCents) : '—';
        const subscriptionLiveText = subscriptionTotalCents > 0 ? this.#formatPrice(subscriptionTotalCents) : '—';
        this.querySelectorAll('[data-bundle-price="one-time"]').forEach((el) => {
          el.textContent = regularLiveText;
        });
        this.querySelectorAll('[data-bundle-price="subscription"]').forEach((el) => {
          el.textContent = subscriptionLiveText;
        });
        this.querySelectorAll('[data-bundle-price-compare]').forEach((el) => {
          el.textContent = regularLiveText;
          el.hidden = regularTotalCents <= subscriptionTotalCents;
        });
      }

      // ── Purchase type ───────────────────────────────────────────────────────────

      #getPurchaseType() {
        if (!this.#selectedVariantHasSellingPlan()) {
          return 'one-time';
        }

        // Native JFFD selling-plan-selector radios use 'subscription'/'ship_once'
        // (shared with the non-bundle selector), normalize to 'subscribe'/'one-time'.
        const checked = this.querySelector('.jffd-bundle-product__purchase-radio:checked');
        return checked?.value === 'subscription' ? 'subscribe' : 'one-time';
      }

      #getSelectedSellingPlan() {
        if (!this.#selectedVariantHasSellingPlan() || this.#getPurchaseType() !== 'subscribe') {
          return null;
        }

        const nativeSelect = this.querySelector('.jffd-bundle-product__selling-plan-select');
        if (nativeSelect instanceof HTMLSelectElement && nativeSelect.value) {
          const selectedOption = nativeSelect.options[nativeSelect.selectedIndex];
          return {
            id: parseInt(nativeSelect.value, 10),
            name: JffdBundleForm.planNameFromOption(selectedOption) || this.dataset.sellingPlanName || '',
          };
        }

        const fallbackId = parseInt(this.dataset.sellingPlanId || '', 10);
        if (Number.isFinite(fallbackId) && fallbackId > 0) {
          return {
            id: fallbackId,
            name: this.dataset.sellingPlanName || '',
          };
        }

        return null;
      }

      // ── Edit mode ──────────────────────────────────────────────────────────────

      #checkUrlParams() {
        const params = new URLSearchParams(window.location.search);
        const variantId = params.get('variant');
        
        if (variantId) {
          // Select variant from URL parameter
          const radio = this.querySelector(
            `.jffd-bundle-product__size-radio[data-variant-id="${variantId}"]`
          );
          if (radio && !radio.checked) {
            radio.checked = true;
            this.#handleSizeChange(radio);
          }
        }
      }

      #updateUrlVariant(variantId) {
        const params = new URLSearchParams(window.location.search);
        params.set('variant', variantId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(
          { variant: variantId },
          '',
          newUrl
        );
      }

      #checkEditMode() {
        const params = new URLSearchParams(window.location.search);
        const editBundleId = params.get('edit_bundle_id');
        if (!editBundleId) return;
        this.querySelectorAll('[data-atc-text]').forEach((el) => {
          el.textContent = 'Save Changes';
        });
        this.#prepopulateFromCart(editBundleId);
      }

      async #prepopulateFromCart(bundleId) {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const bundleItems = (cart.items || []).filter(
            (item) => item.properties?._bundle_id === bundleId
          );
          if (!bundleItems.length) return;

          // Select the matching size radio from the parent line's variant_id.
          const parentItem = bundleItems.find(
            (item) => item.properties?._bundle_is_parent === 'true'
          );
          if (parentItem) {
            this.dataset.editBundleQuantity = String(Math.max(Number(parentItem.quantity) || 1, 1));
            const sizeRadio = this.querySelector(
              `.jffd-bundle-product__size-radio[data-variant-id="${parentItem.variant_id}"]`
            );
            if (sizeRadio && !sizeRadio.checked) {
              sizeRadio.checked = true;
              this.#handleSizeChange(sizeRadio);
            }
          }

          // Restore the purchase option and delivery frequency the bundle was
          // saved with. The plan lives on the child lines (the parent is
          // plan-less); the parent is only consulted for legacy carts.
          const childItems = bundleItems.filter(
            (item) => item.properties?._bundle_is_parent !== 'true'
          );
          const savedPlanId =
            childItems.find((item) => item.selling_plan_allocation?.selling_plan?.id)
              ?.selling_plan_allocation?.selling_plan?.id ??
            parentItem?.selling_plan_allocation?.selling_plan?.id;
          this.#restorePurchaseSelection(savedPlanId);

          // Restore the per-set recipe mix, rather than the aggregate quantity
          // of every bundle set already represented by the cart lines.
          const bundleQuantity = Math.max(Number(parentItem?.quantity) || 1, 1);
          childItems.forEach((item) => {
              const tile = this.querySelector(
                `.jffd-bundle-product__recipe-tile[data-variant-id="${item.variant_id}"]`
              );
              if (!tile) return;
              const quantity = Math.max(Math.round(item.quantity / bundleQuantity), 0);
              tile.dataset.qty = String(quantity);
              const badge = tile.querySelector('[data-qty-badge]');
              if (badge) badge.textContent = String(quantity);
              const qtyDisplay = tile.querySelector('[data-qty-display]');
              if (qtyDisplay) qtyDisplay.textContent = String(quantity);
            });

          this.#updateTally();
        } catch (err) {
          console.error('[jffd-bundle-form] prepopulateFromCart failed', err);
        }
      }

      #restorePurchaseSelection(sellingPlanId) {
        const subscriptionRadio = this.querySelector(
          '.jffd-bundle-product__purchase-radio[value="subscription"]'
        );
        const shipOnceRadio = this.querySelector(
          '.jffd-bundle-product__purchase-radio[value="ship_once"]'
        );
        const select = this.querySelector('.jffd-bundle-product__selling-plan-select');

        if (sellingPlanId && subscriptionRadio && !subscriptionRadio.disabled) {
          subscriptionRadio.checked = true;
          if (select instanceof HTMLSelectElement) {
            const match = Array.from(select.options).find(
              (option) => option.value === String(sellingPlanId)
            );
            if (match) select.value = match.value;
          }
        } else if (!sellingPlanId && shipOnceRadio && !shipOnceRadio.disabled) {
          shipOnceRadio.checked = true;
        }

        this.#syncPriceDisplay();
      }

      // ── Cart items ──────────────────────────────────────────────────────────────

      #buildCartItems() {
        const parentVariantId = this.dataset.parentVariantId;
        const purchaseType = this.#getPurchaseType();
        const selectedPlan = this.#getSelectedSellingPlan();
        const isSubscribe = purchaseType === 'subscribe' && selectedPlan?.id && parentVariantId;
        const bundleQuantity = this.#isCartUpdateEnabled()
          ? Math.max(Number(this.dataset.editBundleQuantity) || 1, 1)
          : 1;

        if (!parentVariantId) return [];

        const bundleKey =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : Date.now().toString(36) + Math.random().toString(36).slice(2);

        const checkedSize = this.querySelector('.jffd-bundle-product__size-radio:checked');
        const variantName =
          checkedSize
            ?.closest('label')
            ?.querySelector('.jffd-bundle-product__size-name')
            ?.textContent?.trim() || '';

        const activeTiles = this.#getVisibleTiles().filter(
          (tile) => (parseInt(tile.dataset.qty, 10) || 0) > 0
        );

        // Note: _bundle_selling_plan_name is intentionally NOT in the shared props.
        // It is added only when the customer chooses Subscribe & Save (see below),
        // otherwise a one-time bundle would still display the plan name in the cart.
        const sharedProps = {
          _bundle_id: bundleKey,
          _bundle_url: window.location.pathname,
          _bundle_product_title: this.dataset.productTitle || '',
          _bundle_product_image_url: this.dataset.productImageUrl || '',
          _bundle_variant_name: variantName,
        };

        // Parent line — the $0 size variant becomes a real cart line so Shopify
        // can nest the recipe children under it natively.
        const parentItem = {
          id: Number(parentVariantId),
          quantity: bundleQuantity,
          properties: { ...sharedProps, _bundle_is_parent: 'true' },
        };
        // The parent line is a plain one-time $0 line: it carries the _bundle_*
        // display properties only — never `selling_plan` and never
        // __og_parent_product_id. Ordergroove turns every line sharing that
        // property value into a bundle component (a parent carrying its own key
        // became a component of itself, order JW1730) and creates a separate
        // subscription for any plan-bearing line without the key (a plan-bearing
        // parent produced a stray $0 standalone subscription, order JW1744).
        // OG derives the bundle parent from the children's property value.
        if (isSubscribe) {
          parentItem.properties._bundle_selling_plan_name = selectedPlan.name || '';
        }

        const childItems = activeTiles.map((tile) => {
          const item = {
            id: tile.dataset.variantId,
            quantity: parseInt(tile.dataset.qty, 10) * bundleQuantity,
            parent_id: Number(parentVariantId),
            properties: { ...sharedProps },
          };
          if (isSubscribe) {
            item.selling_plan = Number(selectedPlan.id);
            item.properties._bundle_selling_plan_name = selectedPlan.name || '';
            item.properties.__og_parent_product_id = `${parentVariantId}:${bundleKey}`;
          }
          return item;
        });

        return [parentItem, ...childItems];
      }

      // ── Merge into an existing bundle ───────────────────────────────────────────

      #isCartUpdateEnabled() {
        return this.querySelector('[data-selling-plan-cart-update="true"]') !== null;
      }

      // Finds a cart bundle group built from the same size variant and the same
      // per-bundle recipe mix. `planMatches` tells the caller whether every line
      // in the group is already on the currently selected selling plan.
      #findMatchingBundleGroup(cart, items) {
        const parentVariantId = Number(items[0].id);
        // The parent line never carries a plan; the bundle's plan lives on the children.
        const sellingPlanId = items[1]?.selling_plan ? Number(items[1].selling_plan) : null;
        const addedBundleQuantity = Number(items[0].quantity) || 1;
        const desired = new Map(
          items.slice(1).map((item) => [String(item.id), Number(item.quantity) / addedBundleQuantity])
        );
        if (!desired.size) return null;

        const groups = new Map();
        (cart.items || []).forEach((line) => {
          const bundleId = line.properties?._bundle_id;
          if (!bundleId) return;
          if (!groups.has(bundleId)) groups.set(bundleId, []);
          groups.get(bundleId).push(line);
        });

        for (const [bundleId, lines] of groups) {
          const parent = lines.find((line) => line.properties?._bundle_is_parent === 'true');
          if (!parent || Number(parent.variant_id) !== parentVariantId) continue;

          const children = lines.filter((line) => line.properties?._bundle_is_parent !== 'true');
          if (children.length !== desired.size) continue;

          const parentQty = parent.quantity || 1;
          const sameMix = children.every(
            (child) => (desired.get(String(child.variant_id)) || 0) * parentQty === child.quantity
          );
          if (!sameMix) continue;

          const hasSelectedPlan = (line) => {
            const linePlanId = line.selling_plan_allocation?.selling_plan?.id ?? null;
            return (linePlanId ? Number(linePlanId) : null) === sellingPlanId;
          };
          const planMatches = children.every(hasSelectedPlan);
          // Legacy carts built before the parent line went plan-less still carry
          // the plan on the parent; callers clear it.
          const parentOnPlan = Boolean(parent.selling_plan_allocation);

          return {
            bundleId,
            parent,
            children,
            parentQty,
            desired,
            planMatches,
            addedBundleQuantity,
            parentOnPlan,
          };
        }

        return null;
      }

      // Same plan: add the requested bundle quantity. Different plan: move the
      // existing bundle onto the newly selected plan with that same quantity.
      async #updateExistingBundle(group, items, sectionIds) {
        const parentVariantId = items[0].id;
        // Plan comes from the children — the parent line is always plan-less.
        const sellingPlanId = items[1]?.selling_plan || null;
        const sellingPlanName = items[1]?.properties?._bundle_selling_plan_name || '';

        if (group.planMatches) {
          const body = {
            updates: {
              [group.parent.key]: group.parentQty + group.addedBundleQuantity,
              ...Object.fromEntries(
                group.children.map((child) => [
                  child.key,
                  child.quantity +
                    (group.desired.get(String(child.variant_id)) || 0) * group.addedBundleQuantity,
                ])
              ),
            },
            ...(sectionIds.length && {
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          };
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || data.status || !group.parentOnPlan) {
            return { ok: response.ok && !data.status, data };
          }

          // Legacy cart: the parent still carries a selling plan from before the
          // parent line went plan-less. Clear it so Ordergroove doesn't create a
          // stray $0 standalone subscription for it. /cart/update.js keeps line
          // keys stable, so the parent key is still valid here.
          const healResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: group.parent.key,
              quantity: group.parentQty + group.addedBundleQuantity,
              selling_plan: null,
              ...(sectionIds.length && {
                sections: sectionIds.join(','),
                sections_url: window.location.pathname,
              }),
            }),
          });
          const healData = await healResponse.json();
          return { ok: healResponse.ok && !healData.status, data: healData };
        }

        const lines = [
          ...group.children.map((child) => ({
            line: child,
            quantity:
              (group.desired.get(String(child.variant_id)) || 0) * group.addedBundleQuantity,
          })),
          { line: group.parent, quantity: group.addedBundleQuantity },
        ];

        let lastResponse = null;
        for (const { line, quantity } of lines) {
          const body = {
            id: line.key,
            quantity,
            ...(sectionIds.length && {
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          };

          if (!group.planMatches) {
            const properties = { ...(line.properties || {}) };
            const isParentLine = properties._bundle_is_parent === 'true';
            if (sellingPlanId) {
              properties._bundle_selling_plan_name = sellingPlanName;
              // Only children carry the OG grouping key (see #buildCartItems).
              if (isParentLine) {
                delete properties.__og_parent_product_id;
              } else {
                properties.__og_parent_product_id = `${parentVariantId}:${group.bundleId}`;
              }
            } else {
              delete properties._bundle_selling_plan_name;
              delete properties.__og_parent_product_id;
            }
            // The parent line is always plan-less; the explicit null also clears
            // the plan from legacy carts whose parent still carries one.
            body.selling_plan = isParentLine ? null : sellingPlanId;
            body.properties = properties;
          }

          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });

          const data = await response.json();
          if (!response.ok || data.status) return { ok: false, data };
          lastResponse = data;
        }

        return { ok: true, data: lastResponse };
      }

      async #removeBundleGroup(cart, bundleId) {
        const bundleLines = (cart.items || []).filter(
          (line) => line.properties?._bundle_id === bundleId
        );
        const lines = [
          ...bundleLines.filter((line) => line.properties?._bundle_is_parent !== 'true'),
          ...bundleLines.filter((line) => line.properties?._bundle_is_parent === 'true'),
        ].filter((line) => line.key);
        if (!lines.length) {
          return {
            ok: false,
            data: { message: 'Could not find the bundle to update in your cart.' },
          };
        }
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: Object.fromEntries(lines.map((line) => [line.key, 0])),
          }),
        });
        const data = await response.json();
        return { ok: response.ok && !data.status, data };
      }

      // ── Add to cart ─────────────────────────────────────────────────────────────

      async #handleAddToCart(event) {
        event.preventDefault();

        const tally = this.#getCurrentTally();
        const slotCount = this.#getSlotCount();
        if (tally !== slotCount) return;

        const allBtns = this.querySelectorAll('[data-atc-button]');
        allBtns.forEach((b) => b.setAttribute('aria-busy', 'true'));
        this.querySelectorAll('[data-error-region]').forEach((el) => el.classList.add('hidden'));

        const items = this.#buildCartItems();
        if (!items.length) {
          this.#showError('Please select recipes before adding to cart.');
          allBtns.forEach((b) => b.removeAttribute('aria-busy'));
          return;
        }

        const editParams = new URLSearchParams(window.location.search);
        const editBundleId = editParams.get('edit_bundle_id');

        // Collect cart-items-component section IDs so /cart/add.js returns
        // rendered HTML for each section in the response. This lets
        // component-cart-items.js morph the DOM synchronously on cart:update,
        // so that jffd-bundle-cart.js finds the new bundle rows in its rAF.
        const sectionIds = [
          ...new Set(
            [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
              (el) => el.dataset.sectionId
            )
          ),
        ].filter(Boolean);

        try {
          let data = null;

          // Remove the complete edited group first, so a newly edited recipe can
          // merge into an identical bundle that was already in the cart.
          let cart = null;
          if (editBundleId) {
            cart = await (await fetch('/cart.js')).json();
            const removal = await this.#removeBundleGroup(cart, editBundleId);
            if (!removal.ok) {
              this.#showError(
                removal.data?.description ||
                  removal.data?.message ||
                  'Could not update the bundle in your cart. Please try again.'
              );
              return;
            }
            cart = await (await fetch('/cart.js')).json();
          }

          // Same size + recipe mix already in the cart: bump its quantity when the
          // frequency matches, otherwise switch that line to the new frequency.
          if (this.#isCartUpdateEnabled()) {
            cart ||= await (await fetch('/cart.js')).json();
            const group = this.#findMatchingBundleGroup(cart, items);
            if (group) {
              const result = await this.#updateExistingBundle(group, items, sectionIds);
              if (!result.ok) {
                this.#showError(
                  result.data?.description ||
                    result.data?.message ||
                    'Could not update the bundle in your cart. Please try again.'
                );
                return;
              }
              data = result.data;
            }
          }

          if (!data) {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items,
                ...(sectionIds.length && {
                  sections: sectionIds.join(','),
                  sections_url: window.location.pathname,
                }),
              }),
            });

            data = await response.json();

            if (!response.ok || data.status) {
              const message =
                data.description ||
                data.message ||
                (typeof data.errors === 'string' ? data.errors : null) ||
                'Could not add bundle to cart. Please try again.';
              this.#showError(message);
              return;
            }
          }

          // Redirect after an edit instead of opening the cart drawer.
          if (editBundleId) {
            window.location.href = '/cart';
          } else {
            // Open the cart drawer directly (works regardless of auto-open setting).
            const cartDrawer = document.querySelector('cart-drawer-component');
            if (cartDrawer?.open) cartDrawer.open();
          }

          // Fetch updated cart count for the icon badge update.
          let cartItemCount = 0;
          try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            // Logical count: subtract bundle child quantities so each bundle = 1
            const bundleChildQty = (cart.items || []).reduce((sum, item) => {
              const props = item.properties || {};
              return props._bundle_id && props._bundle_is_parent !== 'true'
                ? sum + (item.quantity || 0)
                : sum;
            }, 0);
            cartItemCount = (cart.item_count || 0) - bundleChildQty;
          } catch (_) {}

          // Dispatch cart:update with sections HTML so component-cart-items
          // morphs the DOM before jffd-bundle-cart.js's rAF runs.
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: data,
                sourceId: 'jffd-bundle-form',
                data: {
                  source: 'jffd-bundle-form',
                  itemCount: cartItemCount,
                  sections: data.sections ?? {},
                },
              },
            })
          );

          this.dispatchEvent(
            new CustomEvent('jffd:bundle:added', {
              bubbles: true,
              detail: { items: data.items || data },
            })
          );

          allBtns.forEach((btn) => {
            const textEl = btn.querySelector('[data-atc-text]');
            const originalText = textEl ? textEl.textContent : null;
            btn.dataset.added = 'true';
            if (textEl) textEl.textContent = window.theme_strings?.added_to_cart || 'Added!';
            setTimeout(() => {
              delete btn.dataset.added;
              if (textEl && originalText) textEl.textContent = originalText;
            }, 1400);
          });
        } catch (err) {
          this.#showError('An unexpected error occurred. Please try again.');
          console.error('[jffd-bundle-form]', err);
        } finally {
          allBtns.forEach((b) => b.removeAttribute('aria-busy'));
        }
      }

      #showError(message) {
        const errorEls = this.querySelectorAll('[data-error-region]');
        if (!errorEls.length) return;
        errorEls.forEach((el) => {
          el.textContent = message;
          el.classList.remove('hidden');
        });
        setTimeout(() => {
          errorEls.forEach((el) => el.classList.add('hidden'));
        }, 8000);
      }
    }

    if (!customElements.get('jffd-bundle-form')) {
      customElements.define('jffd-bundle-form', JffdBundleForm);
    }
  })();
